import { Currency, CurrencyAmount, Fraction } from '@kodiak-finance/sdk-core'
import { LightCard } from 'components/Card'
import Column, { AutoColumn } from 'components/Column'
import DoubleCurrencyLogo from 'components/DoubleLogo'
import { RowBetween, RowFlat } from 'components/Row'
import ConfirmationTransactionStep, {
  ConfirmationTransactionStepProps,
} from 'components/StepLine/common/ConfirmationTransactionStep'
import { StepRequired } from 'components/StepLine/common/Step'
import { useStepLineNext } from 'components/StepLine/context/hooks/useStepLineNext'
import { useContext } from 'react'
import { Text } from 'rebass'
import { Field } from 'state/islands/mint/actions'
import { ThemeContext } from 'styled-components'
import { Separator, ThemedText } from 'theme'

import { ConfirmAddModalBottom } from '../../ConfirmAddModalBottom'

type IslandDepositStepProps = {
  noLiquidity?: boolean
  currencies?: { [field in Field]?: Currency }
  liquidityMinted?: string
  parsedAmounts: { [field in Field]?: CurrencyAmount<Currency> }
  price?: Fraction
  amountSharesMin?: string
  onAdd: () => void
  useAutoCompound?: boolean
  expectedShares?: string
  vaultSymbol?: string
  isLoading?: boolean
  apr?: number
  isSingleSided?: boolean
  singleSidedCurrency?: Currency
  singleSidedAmount?: CurrencyAmount<Currency>
} & ConfirmationTransactionStepProps

export const useIslandDepositStep = (props: Omit<IslandDepositStepProps, 'onConfirm'>): StepRequired => {
  const { condition, next } = useStepLineNext('IslandDepositStepDone')
  return {
    title: props.useAutoCompound ? 'Confirm Deposit with Auto-Compound' : 'Confirm Deposit',
    condition,
    component: <IslandDepositStep onConfirm={next} {...props} />,
  }
}

const IslandDepositStep = ({
  noLiquidity,
  currencies,
  liquidityMinted,
  amountSharesMin,
  price,
  parsedAmounts,
  attemptingTxn,
  pendingText,
  hash,
  onAdd,
  onConfirm,
  autoConfirm,
  useAutoCompound,
  expectedShares,
  vaultSymbol,
  isLoading,
  apr,
  isSingleSided,
  singleSidedCurrency,
  singleSidedAmount,
}: // baseApr,
IslandDepositStepProps) => {
  const theme = useContext(ThemeContext)
  if (!currencies) {
    return <></>
  }
  return (
    <ConfirmationTransactionStep
      attemptingTxn={attemptingTxn}
      pendingText={pendingText}
      hash={hash}
      transactionWaiting={true}
      onConfirm={onConfirm}
      autoConfirm={autoConfirm}
    >
      <Column width="100%" gap="8px">
        {noLiquidity ? (
          <Column width="100%" gap="20px">
            <LightCard mt="20px" $borderRadius="20px">
              <RowFlat>
                <Text fontSize="48px" fontWeight={500} lineHeight="42px" marginRight={10}>
                  {currencies[Field.CURRENCY_A]?.symbol + '/' + currencies[Field.CURRENCY_B]?.symbol}
                </Text>
                <DoubleCurrencyLogo
                  currency0={currencies[Field.CURRENCY_A]}
                  currency1={currencies[Field.CURRENCY_B]}
                  size={30}
                />
              </RowFlat>
            </LightCard>
          </Column>
        ) : (
          <></>
        )}
        {useAutoCompound && (
          <LightCard style={{ marginTop: '16px' }}>
            <AutoColumn gap="8px">
              <RowBetween>
                <ThemedText.DeprecatedSubHeader color={theme.deprecated_text1} fontWeight={500}>
                  Auto-Compound Enabled
                </ThemedText.DeprecatedSubHeader>
                <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_green1}>
                  ✓ Active
                </ThemedText.DeprecatedBlack>
              </RowBetween>

              <RowBetween>
                <ThemedText.DeprecatedSubHeader color={theme.deprecated_text2}>
                  Expected Bault Shares
                </ThemedText.DeprecatedSubHeader>
                <ThemedText.DeprecatedBlack textAlign="right" fontSize={14}>
                  {/* {isLoading ? 'Loading...' : expectedShares ? `${expectedShares} ${vaultSymbol || ''}` : '-'} */}
                  {isLoading ? 'Loading...' : expectedShares ? `${expectedShares} Pool Tokens` : '-'}
                </ThemedText.DeprecatedBlack>
              </RowBetween>

              {apr && (
                <RowBetween>
                  <ThemedText.DeprecatedSubHeader color={theme.deprecated_text2}>
                    Est. APY (with auto-compound)
                  </ThemedText.DeprecatedSubHeader>
                  <ThemedText.DeprecatedBlack textAlign="right" fontSize={14} color={theme.deprecated_green1}>
                    {apr.toFixed(2)}%
                  </ThemedText.DeprecatedBlack>
                </RowBetween>
              )}

              <Separator />

              <ThemedText.DeprecatedBody fontSize={14} color={theme.deprecated_text3}>
                Your LP tokens will be automatically staked in the Bault, where rewards are harvested and reinvested to
                maximize your yield.
              </ThemedText.DeprecatedBody>
            </AutoColumn>
          </LightCard>
        )}
        <ConfirmAddModalBottom
          price={price}
          currencies={currencies}
          parsedAmounts={parsedAmounts}
          noLiquidity={noLiquidity}
          onAdd={onAdd}
          liquidityMinted={liquidityMinted}
          amountSharesMin={amountSharesMin}
          isSingleSided={isSingleSided}
          singleSidedCurrency={singleSidedCurrency}
          singleSidedAmount={singleSidedAmount}
          useAutoCompound={useAutoCompound}
        />
      </Column>
    </ConfirmationTransactionStep>
  )
}

export default IslandDepositStep
